import { useSeerRating, POLLING_INTERVALS_MILLIS } from 'common';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DashboardCard } from './DashboardCard';
import { hvacCompanyLogo, hvacIcon } from '../../../assets';
import { useAppSync } from '../../../contexts/AppSyncContext';
import { ROUTE_PATHNAMES } from '../../../routes/routeConstants';
import {
  Status,
  getGlowColors,
  getGradientColors,
  getIconBgColor,
  getIconColor,
  getStatusTextColor,
} from '../../../types/Status';

const mapSeerRatingToStatus = (seerValue: number, maxValue: number = 18): Status => {
  const step = maxValue / 9;
  const segmentIndex = Math.floor(seerValue / step);

  if (segmentIndex < 6) return Status.Poor;
  return Status.Good;
};

const useHVACCardStatus = () => {
  const { selectedHubId } = useAppSync();
  const { data: seerData, fetchSeerRating } = useSeerRating();
  const { t } = useTranslation();
  const lastFetchTimeRef = useRef<number>(0);

  useEffect(() => {
    if (selectedHubId) {
      const now = Date.now();

      // Check if it's been at least 30 seconds since the last fetch
      if (now - lastFetchTimeRef.current > POLLING_INTERVALS_MILLIS.HVAC) {
        fetchSeerRating(selectedHubId);
        lastFetchTimeRef.current = now;
      }

      // Set up a polling interval to refresh data every 30 seconds
      const interval = setInterval(() => {
        fetchSeerRating(selectedHubId);
        lastFetchTimeRef.current = Date.now();
      }, POLLING_INTERVALS_MILLIS.HVAC);

      return () => clearInterval(interval);
    }
  }, [selectedHubId]);

  const seerValue = seerData?.lastMinuteSeerRating?.seerValue
    ? typeof seerData.lastMinuteSeerRating.seerValue === 'string' &&
      seerData.lastMinuteSeerRating.seerValue !== '-'
      ? parseFloat(seerData.lastMinuteSeerRating.seerValue)
      : 0
    : 0;
  const isRunning = seerData?.hvacRunning ?? false;
  const status = isRunning ? mapSeerRatingToStatus(seerValue) : Status.Good;

  const displayValue = isRunning
    ? `${t(`devices.status.${status}`)} (${seerValue < 1 ? '-' : seerValue.toFixed(1)})`
    : t('hvac.systemOff');

  return {
    status,
    seerValue: displayValue,
    hvacRunning: seerData?.hvacRunning ?? false,
  };
};

export const HVACCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { status, seerValue } = useHVACCardStatus();

  return (
    <DashboardCard
      iconSrc={hvacIcon}
      iconBgColor={getIconBgColor(status)}
      iconColor={getIconColor(status)}
      brandLogoSrc={hvacCompanyLogo}
      bottomPrimaryText={seerValue}
      bottomPrimaryTextColor={getStatusTextColor(status)}
      bottomSecondaryText={t('hvac.seerRating')}
      backgroundGradientColors={getGradientColors(status)}
      glowGradientColors={getGlowColors(status)}
      onPress={() => navigate(ROUTE_PATHNAMES.hvac)}
    />
  );
};
